import { clsx } from "clsx";
import { Tooltip } from "@/components/ui/Tooltip";
import { VOTE_LABELS } from "../types";
import type { BacklogVote, BacklogVoteValue } from "../types";

const VOTE_ORDER: BacklogVoteValue[] = ["plus", "minus", "neutral", "later"];

const VOTE_GLYPHS: Record<BacklogVoteValue, string> = {
  plus: "+",
  minus: "−",
  neutral: "0",
  later: "⏳",
};

const ACTIVE_STYLES: Record<BacklogVoteValue, string> = {
  plus: "border-green-600 text-green-700 bg-green-50",
  minus: "border-red-600 text-red-700 bg-red-50",
  neutral: "border-gray-500 text-gray-700 bg-gray-100",
  later: "border-amber-600 text-amber-700 bg-amber-50",
};

interface Props {
  votes: BacklogVote[];
  currentUserId: string;
  /** Clicking your current vote retracts it. Omit to render read-only. */
  onVote?: (value: BacklogVoteValue | null) => void;
  disabled?: boolean;
}

export function VoteBar({ votes, currentUserId, onVote, disabled }: Props) {
  const myVote = votes.find((v) => v.user_id === currentUserId)?.value ?? null;

  return (
    <div className="flex items-center gap-1">
      {VOTE_ORDER.map((value) => {
        const voters = votes.filter((v) => v.value === value);
        const isMine = myVote === value;
        const label = voters.length
          ? `${VOTE_LABELS[value]}: ${voters.map((v) => v.user_name).join(", ")}`
          : VOTE_LABELS[value];
        return (
          <Tooltip key={value} content={label}>
            <button
              type="button"
              disabled={!onVote || disabled}
              onClick={(e) => {
                e.stopPropagation();
                onVote?.(isMine ? null : value);
              }}
              className={clsx(
                "px-2 py-0.5 text-xs font-mono border rounded-sm transition-colors duration-100",
                isMine
                  ? ACTIVE_STYLES[value]
                  : "border-[var(--color-border)] text-[var(--color-text-muted)]",
                onVote && !disabled && "hover:border-[var(--color-border-strong)]",
              )}
            >
              {VOTE_GLYPHS[value]}
              {voters.length > 0 && <span className="ml-1">{voters.length}</span>}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}
